import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Check, X, MessageSquare, Download, Loader2, FileText, Clock, User, Sparkles, Plus, Link2, Send, Calendar } from 'lucide-react';
import { formatCurrency, formatDate, formatDateShort, formatInvoiceStatus, getInvoiceStatusColor, formatPaymentStatus, getPaymentStatusColor } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import WorkflowVisualizer from '@/components/WorkflowVisualizer';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert axios error -> readable string. FastAPI 422 returns `detail` as a
// list of validation errors, which sonner's toast cannot render — passing it
// raw can crash the React tree.
const formatAxiosError = (e, fallback = 'Erreur') => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  return fallback;
};

const PAYMENT_MODES = [
  { value: 'virement', label: 'Virement' },
  { value: 'prelevement', label: 'Prélèvement SEPA' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const tomorrowISO = () => {
  const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10);
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [canValidate, setCanValidate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [pickedSupplierId, setPickedSupplierId] = useState('');
  const [proposedDraft, setProposedDraft] = useState(null);
  const [supplierBusy, setSupplierBusy] = useState(false);
  // Payment actions (validée non payée)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ date_paiement: todayISO(), mode_paiement: 'virement', reference_paiement: '' });
  const [payBusy, setPayBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_date: tomorrowISO(), mode_paiement: 'prelevement', bank_account_id: '', notes: '' });
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [sepaOpen, setSepaOpen] = useState(false);
  const [sepaForm, setSepaForm] = useState({ date_execution: tomorrowISO(), bank_account_id: '' });
  const [sepaBusy, setSepaBusy] = useState(false);
  const [postSepaOpen, setPostSepaOpen] = useState(false);
  const [postSepa, setPostSepa] = useState(null);
  const [postSepaBusy, setPostSepaBusy] = useState(false);
  // Available avoirs for the invoice's supplier (loaded when opening pay/SEPA dialogs)
  const [availableAvoirs, setAvailableAvoirs] = useState([]);
  const [avoirSelections, setAvoirSelections] = useState({}); // { avoir_id: amount }

  const fetchData = async () => {
    try {
      const [invRes, timeRes] = await Promise.all([
        axios.get(`${API_URL}/supplier-invoices/${id}`),
        axios.get(`${API_URL}/validation/${id}/timeline`).catch(() => ({ data: [] }))
      ]);
      setInvoice(invRes.data);
      setTimeline(timeRes.data);
      if (invRes.data.workflow_id) {
        try {
          const wfRes = await axios.get(`${API_URL}/workflows/${invRes.data.workflow_id}`);
          setWorkflow(wfRes.data);
        } catch (e) {}
      }
      if (invRes.data.workflow_status === 'en_validation') {
        const pending = await axios.get(`${API_URL}/validation/pending`).catch(() => ({ data: [] }));
        setCanValidate(pending.data.some(p => p.id === id));
      }
    } catch (e) { toast.error('Erreur chargement facture'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [id]);

  // Initialise the editable proposed-supplier draft when an invoice without a
  // supplier_id but with a proposal lands on the page.
  useEffect(() => {
    if (invoice && !invoice.supplier_id && invoice.proposed_supplier) {
      setProposedDraft({ ...invoice.proposed_supplier });
    } else {
      setProposedDraft(null);
    }
  }, [invoice?.id, invoice?.supplier_id]);

  useEffect(() => {
    if (!invoice || invoice.supplier_id) return;
    if (invoice.workflow_status !== 'brouillon' && invoice.workflow_status !== 'modification_demandee') return;
    axios.get(`${API_URL}/suppliers/`)
      .then(res => setSuppliers(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [invoice?.id, invoice?.supplier_id, invoice?.workflow_status]);

  const handleConfirmProposedSupplier = async () => {
    if (!proposedDraft?.nom?.trim()) { toast.error('Le nom du fournisseur est obligatoire'); return; }
    setSupplierBusy(true);
    try {
      await axios.post(`${API_URL}/supplier-invoices/${id}/confirm-proposed-supplier`, {
        nom: proposedDraft.nom,
        siret: proposedDraft.siret || null,
        adresse: proposedDraft.adresse || null,
        code_postal: proposedDraft.code_postal || null,
        ville: proposedDraft.ville || null,
        pays: proposedDraft.pays || 'France',
        telephone: proposedDraft.telephone || null,
        email: proposedDraft.email || null,
        iban: proposedDraft.iban || null,
        bic: proposedDraft.bic || null,
        tva_intracommunautaire: proposedDraft.tva_intracommunautaire || null,
      });
      toast.success('Fournisseur créé et lié à la facture');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur création fournisseur')); }
    finally { setSupplierBusy(false); }
  };

  const handleLinkExistingSupplier = async () => {
    if (!pickedSupplierId) { toast.error('Sélectionnez un fournisseur'); return; }
    setSupplierBusy(true);
    try {
      await axios.post(`${API_URL}/supplier-invoices/${id}/link-supplier`, { supplier_id: pickedSupplierId });
      toast.success('Fournisseur lié à la facture');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur liaison')); }
    finally { setSupplierBusy(false); }
  };

  // --- Payment actions for validée invoices --------------------------------
  useEffect(() => {
    if (!invoice || invoice.workflow_status !== 'validee') return;
    axios.get(`${API_URL}/bank-accounts/`)
      .then(res => {
        const accs = Array.isArray(res.data) ? res.data : [];
        setBankAccounts(accs);
        const def = accs.find(a => a.is_default) || accs[0];
        if (def?.id) {
          setSepaForm(f => ({ ...f, bank_account_id: f.bank_account_id || def.id }));
          setScheduleForm(f => ({ ...f, bank_account_id: f.bank_account_id || def.id }));
        }
      })
      .catch(() => {});
  }, [invoice?.id, invoice?.workflow_status]);

  // Load unused/partial avoirs for the supplier when pay/SEPA dialogs open
  const loadAvailableAvoirs = async () => {
    if (!invoice?.supplier_id) { setAvailableAvoirs([]); return; }
    try {
      const res = await axios.get(`${API_URL}/supplier-invoices/avoirs-available/${invoice.supplier_id}`);
      setAvailableAvoirs(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setAvailableAvoirs([]); }
    setAvoirSelections({});
  };

  const toggleAvoir = (avoir) => {
    setAvoirSelections(prev => {
      const next = { ...prev };
      if (avoir.id in next) {
        delete next[avoir.id];
      } else {
        // Default: apply the maximum possible (min of remaining vs invoice TTC remaining)
        const invTtc = Math.abs(invoice?.montant_ttc || 0);
        const totalSelected = Object.values(prev).reduce((s, v) => s + (parseFloat(v) || 0), 0);
        const room = Math.max(0, invTtc - totalSelected);
        next[avoir.id] = Math.min(avoir.avoir_remaining_amount || 0, room).toFixed(2);
      }
      return next;
    });
  };

  const setAvoirAmount = (avoirId, value) => {
    setAvoirSelections(prev => ({ ...prev, [avoirId]: value }));
  };

  const totalDeducted = useMemo(
    () => Object.values(avoirSelections).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [avoirSelections]
  );

  const netAmount = useMemo(() => {
    const ttc = Math.abs(invoice?.montant_ttc || 0);
    return Math.max(0, ttc - totalDeducted);
  }, [invoice, totalDeducted]);

  const appliedAvoirsPayload = () => Object.entries(avoirSelections)
    .map(([avoir_id, amount]) => ({ avoir_id, amount: parseFloat(amount) || 0 }))
    .filter(a => a.amount > 0);

  const openPayDialog = () => {
    setPayForm({
      date_paiement: todayISO(),
      mode_paiement: invoice?.mode_paiement || 'virement',
      reference_paiement: invoice?.reference_paiement || '',
    });
    loadAvailableAvoirs();
    setPayOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!payForm.date_paiement || !payForm.mode_paiement) {
      toast.error('Date et mode de paiement requis');
      return;
    }
    const applied = appliedAvoirsPayload();
    setPayBusy(true);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${id}/payment`, {
        payment_status: 'payee',
        date_paiement: payForm.date_paiement,
        mode_paiement: payForm.mode_paiement,
        reference_paiement: payForm.reference_paiement || null,
        applied_avoirs: applied.length ? applied : null,
      });
      if (applied.length) {
        toast.success(`Facture payée — ${applied.length} avoir(s) déduit(s) (${formatCurrency(totalDeducted)})`);
      } else {
        toast.success('Facture marquée comme payée');
      }
      setPayOpen(false);
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur enregistrement paiement')); }
    finally { setPayBusy(false); }
  };

  const handleUnmarkPaid = async () => {
    if (!window.confirm('Annuler le paiement et remettre la facture en non payée ?')) return;
    try {
      await axios.put(`${API_URL}/supplier-invoices/${id}/payment`, {
        payment_status: 'non_payee',
        date_paiement: null,
        mode_paiement: invoice?.mode_paiement || null,
        reference_paiement: null,
      });
      toast.success('Paiement annulé');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur annulation paiement')); }
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleForm.scheduled_date || !scheduleForm.mode_paiement) {
      toast.error('Date et mode de paiement requis'); return;
    }
    if (scheduleForm.mode_paiement === 'virement_sepa' && !scheduleForm.bank_account_id) {
      toast.error('Compte bancaire requis pour un virement SEPA'); return;
    }
    setScheduleBusy(true);
    try {
      await axios.post(`${API_URL}/scheduled-payments/`, {
        invoice_id: id,
        scheduled_date: scheduleForm.scheduled_date,
        mode_paiement: scheduleForm.mode_paiement,
        bank_account_id: scheduleForm.mode_paiement === 'virement_sepa' ? scheduleForm.bank_account_id : null,
        notes: scheduleForm.notes || null,
      });
      toast.success('Paiement programmé');
      setScheduleOpen(false);
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur programmation')); }
    finally { setScheduleBusy(false); }
  };

  const handleSepaSubmit = async () => {
    if (!sepaForm.bank_account_id) { toast.error('Compte bancaire requis'); return; }
    if (netAmount <= 0 && totalDeducted > 0) {
      toast.error("Les avoirs couvrent toute la facture — utilisez 'Marquer comme payée' plutôt qu'un virement SEPA.");
      return;
    }
    const applied = appliedAvoirsPayload();
    const avoir_applications = applied.length ? { [id]: applied } : null;
    setSepaBusy(true);
    try {
      const res = await axios.post(`${API_URL}/sepa-transfers/`, {
        bank_account_id: sepaForm.bank_account_id,
        date_execution: sepaForm.date_execution,
        invoice_ids: [id],
        avoir_applications,
      });
      const transfer = res.data;
      toast.success(`SEPA généré : ${transfer.reference}`);

      // Auto-download
      try {
        const xmlRes = await axios.get(`${API_URL}/sepa-transfers/${transfer.id}/xml`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([xmlRes.data], { type: 'application/xml' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${transfer.reference || 'sepa'}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (xmlErr) {
        toast.error("XML généré mais téléchargement automatique impossible. Récupérez-le dans Virements SEPA.");
      }

      setSepaOpen(false);
      fetchData();
      setPostSepa({ reference: transfer.reference, date_execution: sepaForm.date_execution });
      setPostSepaOpen(true);
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur génération SEPA')); }
    finally { setSepaBusy(false); }
  };

  const handlePostSepaConfirm = async () => {
    if (!postSepa) return;
    setPostSepaBusy(true);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${id}/payment`, {
        payment_status: 'payee',
        date_paiement: postSepa.date_execution,
        mode_paiement: 'virement_sepa',
        reference_paiement: postSepa.reference || null,
      });
      toast.success('Facture marquée comme payée');
      setPostSepaOpen(false);
      setPostSepa(null);
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur enregistrement paiement')); }
    finally { setPostSepaBusy(false); }
  };

  const handleAction = async (action) => {
    if ((action === 'rejected' || action === 'modification_requested') && !comment.trim()) {
      toast.error('Un commentaire est obligatoire'); return;
    }
    setActionLoading(true);
    try {
      const endpoint = action === 'approved' ? 'approve' : action === 'rejected' ? 'reject' : 'request-modification';
      await axios.post(`${API_URL}/validation/${id}/${endpoint}`, { comment: comment || undefined });
      toast.success(action === 'approved' ? 'Facture approuvée' : action === 'rejected' ? 'Facture rejetée' : 'Modification demandee');
      setComment('');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setActionLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API_URL}/supplier-invoices/${id}/submit`);
      toast.success('Facture soumise a validation');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur soumission')); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!invoice) return <div className="text-center py-12">Facture non trouvee</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/invoices')}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{invoice.internal_ref}</h1>
          <p className="text-slate-500">{invoice.supplier_name} - N° {invoice.invoice_number}</p>
        </div>
        <Badge className={getInvoiceStatusColor(invoice.workflow_status)}>{formatInvoiceStatus(invoice.workflow_status)}</Badge>
        <Badge className={getPaymentStatusColor(invoice.payment_status)}>{formatPaymentStatus(invoice.payment_status)}</Badge>
        {invoice.type_document === 'avoir' && <Badge variant="destructive">AVOIR</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card><CardHeader><CardTitle>Informations</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Fournisseur:</span> <strong>{invoice.supplier_name || <span className="text-amber-700">— non défini</span>}</strong></div>
                <div><span className="text-slate-500">N° Facture:</span> <strong>{invoice.invoice_number}</strong></div>
                <div><span className="text-slate-500">Date facture:</span> {formatDateShort(invoice.date_facture)}</div>
                <div><span className="text-slate-500">Date échéance:</span> {formatDateShort(invoice.date_echeance)}</div>
                {invoice.description && <div className="col-span-2"><span className="text-slate-500">Description:</span> {invoice.description}</div>}
              </div>
            </CardContent>
          </Card>

          {!invoice.supplier_id && proposedDraft && (invoice.workflow_status === 'brouillon' || invoice.workflow_status === 'modification_demandee') && (
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-900">
                  <Sparkles className="w-4 h-4" /> Nouveau fournisseur détecté
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">
                  Ce fournisseur n'est pas dans votre base. Validez sa création pour pouvoir soumettre la facture, ou liez-la à un fournisseur existant.
                </p>
                {hasPermission('suppliers', 'create_from_invoice') ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Nom *</Label><Input value={proposedDraft.nom || ''} onChange={e => setProposedDraft({ ...proposedDraft, nom: e.target.value })} /></div>
                    <div><Label>SIRET</Label><Input value={proposedDraft.siret || ''} onChange={e => setProposedDraft({ ...proposedDraft, siret: e.target.value })} /></div>
                    <div><Label>TVA intracom.</Label><Input value={proposedDraft.tva_intracommunautaire || ''} onChange={e => setProposedDraft({ ...proposedDraft, tva_intracommunautaire: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Adresse</Label><Input value={proposedDraft.adresse || ''} onChange={e => setProposedDraft({ ...proposedDraft, adresse: e.target.value })} /></div>
                    <div><Label>Code postal</Label><Input value={proposedDraft.code_postal || ''} onChange={e => setProposedDraft({ ...proposedDraft, code_postal: e.target.value })} /></div>
                    <div><Label>Ville</Label><Input value={proposedDraft.ville || ''} onChange={e => setProposedDraft({ ...proposedDraft, ville: e.target.value })} /></div>
                    <div><Label>IBAN</Label><Input value={proposedDraft.iban || ''} onChange={e => setProposedDraft({ ...proposedDraft, iban: e.target.value })} /></div>
                    <div><Label>BIC</Label><Input value={proposedDraft.bic || ''} onChange={e => setProposedDraft({ ...proposedDraft, bic: e.target.value })} /></div>
                    <div className="col-span-2 flex gap-2 pt-2">
                      <Button onClick={handleConfirmProposedSupplier} disabled={supplierBusy} className="bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="w-4 h-4 mr-1" /> Valider et créer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    Vous n'avez pas la permission de créer un fournisseur. Liez la facture à un fournisseur existant ou demandez à un administrateur.
                    <div className="mt-2 text-xs space-y-0.5">
                      <div><strong>{proposedDraft.nom}</strong></div>
                      {proposedDraft.siret && <div>SIRET {proposedDraft.siret}</div>}
                      {proposedDraft.adresse && <div>{proposedDraft.adresse}{proposedDraft.code_postal ? `, ${proposedDraft.code_postal} ${proposedDraft.ville || ''}` : ''}</div>}
                    </div>
                  </div>
                )}

                <Separator />
                <div>
                  <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Ou lier à un fournisseur existant</Label>
                  <div className="flex gap-2 mt-2">
                    <Select value={pickedSupplierId} onValueChange={setPickedSupplierId}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nom}{s.siret ? ` — ${s.siret}` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={handleLinkExistingSupplier} disabled={supplierBusy || !pickedSupplierId}>Lier</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card><CardHeader><CardTitle>Lignes de facture</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Qte</TableHead><TableHead>PU HT</TableHead><TableHead>TVA</TableHead><TableHead>Total HT</TableHead></TableRow></TableHeader>
              <TableBody>
                {(invoice.lignes || []).map((l, i) => (
                  <TableRow key={i}><TableCell>{l.description}</TableCell><TableCell>{l.quantite}</TableCell><TableCell>{formatCurrency(l.prix_unitaire_ht)}</TableCell><TableCell>{l.taux_tva}%</TableCell><TableCell>{formatCurrency(l.montant_ht || l.quantite * l.prix_unitaire_ht)}</TableCell></TableRow>
                ))}
              </TableBody></Table>
              <div className="p-4 text-right space-y-1">
                <div>Total HT: <strong>{formatCurrency(invoice.montant_ht)}</strong></div>
                <div>TVA: <strong>{formatCurrency(invoice.montant_tva)}</strong></div>
                <div className="text-lg">Total TTC: <strong className="text-[#0F5156]">{formatCurrency(invoice.montant_ttc)}</strong></div>
              </div>
            </CardContent>
          </Card>

          {(invoice.attachments || []).length > 0 && (
            <Card><CardHeader><CardTitle>Pieces jointes</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {invoice.attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <FileText className="w-5 h-5 text-slate-400" />
                      <span className="flex-1">{att.filename}</span>
                      <Button variant="ghost" size="sm" onClick={() => { const a = document.createElement('a'); a.href = `data:${att.file_type};base64,${att.content_base64}`; a.download = att.filename; a.click(); }}><Download className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {workflow && (
            <Card><CardHeader><CardTitle>Workflow de validation</CardTitle></CardHeader>
              <CardContent>
                <WorkflowVisualizer steps={workflow.steps} currentStepIndex={invoice.current_step_index} workflowStatus={invoice.workflow_status} />
              </CardContent>
            </Card>
          )}

          {invoice.workflow_status === 'brouillon' && (
            <Card><CardContent className="pt-6">
              <Button
                onClick={handleSubmit}
                disabled={!invoice.supplier_id}
                className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
              >Soumettre a validation</Button>
              {!invoice.supplier_id && (
                <p className="text-xs text-amber-700 mt-2">Liez d'abord un fournisseur à la facture.</p>
              )}
            </CardContent></Card>
          )}

          {canValidate && invoice.workflow_status === 'en_validation' && (
            <Card><CardHeader><CardTitle>Actions de validation</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">Etape: <strong>{invoice.current_step_name}</strong></p>
                <Textarea placeholder="Commentaire (obligatoire pour rejet/modification)" value={comment} onChange={e => setComment(e.target.value)} />
                <div className="flex flex-col gap-2">
                  <Button onClick={() => handleAction('approved')} disabled={actionLoading} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-2" /> Approuver</Button>
                  <Button onClick={() => handleAction('rejected')} disabled={actionLoading} variant="destructive"><X className="w-4 h-4 mr-2" /> Rejeter</Button>
                  <Button onClick={() => handleAction('modification_requested')} disabled={actionLoading} variant="outline" className="border-orange-300 text-orange-600"><MessageSquare className="w-4 h-4 mr-2" /> Demander modification</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {invoice.workflow_status === 'validee' && invoice.payment_status !== 'payee' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Paiement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {hasPermission('invoices', 'pay') && (
                  <>
                    <Button onClick={openPayDialog} className="w-full justify-start bg-emerald-600 hover:bg-emerald-700">
                      <Check className="w-4 h-4 mr-2" /> Marquer comme payée
                    </Button>
                    <Button onClick={() => setScheduleOpen(true)} variant="outline" className="w-full justify-start">
                      <Calendar className="w-4 h-4 mr-2" /> Programmer le paiement
                    </Button>
                  </>
                )}
                {hasPermission('tresorerie', 'sepa') && (
                  <Button onClick={() => { loadAvailableAvoirs(); setSepaOpen(true); }} variant="outline" className="w-full justify-start text-indigo-700 border-indigo-300">
                    <Send className="w-4 h-4 mr-2" /> Générer fichier SEPA (XML)
                  </Button>
                )}
                {!hasPermission('invoices', 'pay') && !hasPermission('tresorerie', 'sepa') && (
                  <p className="text-sm text-slate-500">Permission requise pour le paiement.</p>
                )}
              </CardContent>
            </Card>
          )}

          {invoice.workflow_status === 'validee' && invoice.payment_status === 'payee' && (
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
                  <Check className="w-4 h-4" /> Facture payée
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2 text-xs text-emerald-700">
                  <div><span className="text-emerald-500">Date</span><br/><strong>{invoice.date_paiement || '-'}</strong></div>
                  <div><span className="text-emerald-500">Mode</span><br/><strong>{invoice.mode_paiement || '-'}</strong></div>
                  <div><span className="text-emerald-500">Référence</span><br/><strong>{invoice.reference_paiement || '-'}</strong></div>
                </div>
                {hasPermission('invoices', 'pay') && (
                  <Button onClick={handleUnmarkPaid} size="sm" variant="outline" className="w-full mt-2 text-emerald-800 border-emerald-300">
                    Annuler le paiement (rétablir non payée)
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <Card><CardHeader><CardTitle>Historique</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {timeline.map((action, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="mt-1"><div className={`w-2 h-2 rounded-full ${action.action === 'approved' || action.action === 'submitted' ? 'bg-green-500' : action.action === 'rejected' ? 'bg-red-500' : 'bg-orange-500'}`} /></div>
                    <div>
                      <div className="font-medium">{action.action === 'submitted' ? 'Soumise' : action.action === 'approved' ? 'Approuvée' : action.action === 'rejected' ? 'Rejetée' : action.action === 'skipped' ? 'Ignorée' : 'Modification demandee'}</div>
                      <div className="text-slate-500">{action.user_name} - {action.step_name}</div>
                      {action.comment && <div className="text-slate-600 italic mt-1">"{action.comment}"</div>}
                      <div className="text-xs text-slate-400">{formatDate(action.timestamp)}</div>
                    </div>
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-slate-500 text-sm">Aucune action</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark-as-paid dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle><Check className="w-5 h-5 inline mr-2 text-emerald-600" />Marquer comme payée</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date de paiement *</Label><Input type="date" value={payForm.date_paiement} onChange={e => setPayForm({ ...payForm, date_paiement: e.target.value })} /></div>
            <div><Label>Mode de paiement *</Label>
              <Select value={payForm.mode_paiement} onValueChange={v => setPayForm({ ...payForm, mode_paiement: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Référence</Label><Input value={payForm.reference_paiement} onChange={e => setPayForm({ ...payForm, reference_paiement: e.target.value })} placeholder="N° de virement, chèque..." /></div>

            {availableAvoirs.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2">
                <p className="text-sm font-medium text-emerald-900">Avoirs disponibles pour ce fournisseur</p>
                <p className="text-xs text-emerald-700">Cochez les avoirs à imputer. Les avoirs cochés seront marqués comme utilisés.</p>
                {availableAvoirs.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1">
                    <input type="checkbox" checked={a.id in avoirSelections} onChange={() => toggleAvoir(a)} className="w-4 h-4" />
                    <div className="flex-1 text-xs">
                      <div className="font-medium">{a.internal_ref || a.invoice_number} — {formatCurrency(a.avoir_remaining_amount)} disponible</div>
                      {a.date_facture && <div className="text-slate-500">{a.date_facture}</div>}
                    </div>
                    {a.id in avoirSelections && (
                      <Input type="number" step="0.01" min="0" max={a.avoir_remaining_amount}
                        value={avoirSelections[a.id]}
                        onChange={(e) => setAvoirAmount(a.id, e.target.value)}
                        className="w-24 h-8 text-xs" />
                    )}
                  </div>
                ))}
                <div className="border-t border-emerald-200 pt-2 mt-2 text-xs">
                  <div className="flex justify-between"><span>TTC facture</span><strong>{formatCurrency(Math.abs(invoice?.montant_ttc || 0))}</strong></div>
                  <div className="flex justify-between text-emerald-800"><span>Avoirs imputés</span><strong>− {formatCurrency(totalDeducted)}</strong></div>
                  <div className="flex justify-between mt-1 border-t border-emerald-300 pt-1"><span>Reste à payer</span><strong className="text-emerald-900">{formatCurrency(netAmount)}</strong></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annuler</Button>
            <Button onClick={handleMarkPaid} disabled={payBusy} className="bg-emerald-600 hover:bg-emerald-700">
              {payBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule payment dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Calendar className="w-5 h-5 inline mr-2 text-blue-600" />Programmer le paiement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date d'exécution *</Label><Input type="date" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })} /></div>
            <div><Label>Mode de paiement *</Label>
              <Select value={scheduleForm.mode_paiement} onValueChange={v => setScheduleForm({ ...scheduleForm, mode_paiement: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement_sepa">Virement SEPA</SelectItem>
                  <SelectItem value="prelevement">Prélèvement SEPA</SelectItem>
                  <SelectItem value="virement">Virement manuel</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scheduleForm.mode_paiement === 'virement_sepa' && (
              <div><Label>Compte bancaire émetteur *</Label>
                <Select value={scheduleForm.bank_account_id} onValueChange={v => setScheduleForm({ ...scheduleForm, bank_account_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choisir un compte" /></SelectTrigger>
                  <SelectContent>{bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.label || b.titulaire} — {b.iban}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Notes</Label><Textarea value={scheduleForm.notes} onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })} placeholder="Optionnel" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Annuler</Button>
            <Button onClick={handleScheduleSubmit} disabled={scheduleBusy} className="bg-blue-600 hover:bg-blue-700">
              {scheduleBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Programmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEPA generation dialog */}
      <Dialog open={sepaOpen} onOpenChange={setSepaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle><Send className="w-5 h-5 inline mr-2 text-indigo-600" />Générer fichier SEPA (XML)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date d'exécution *</Label><Input type="date" value={sepaForm.date_execution} onChange={e => setSepaForm({ ...sepaForm, date_execution: e.target.value })} /></div>
            <div><Label>Compte bancaire émetteur *</Label>
              <Select value={sepaForm.bank_account_id} onValueChange={v => setSepaForm({ ...sepaForm, bank_account_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choisir un compte" /></SelectTrigger>
                <SelectContent>{bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.label || b.titulaire} — {b.iban}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {availableAvoirs.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2">
                <p className="text-sm font-medium text-emerald-900">Avoirs disponibles pour ce fournisseur</p>
                <p className="text-xs text-emerald-700">Les avoirs cochés réduisent le montant viré (le XML porte le net) et seront marqués utilisés.</p>
                {availableAvoirs.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1">
                    <input type="checkbox" checked={a.id in avoirSelections} onChange={() => toggleAvoir(a)} className="w-4 h-4" />
                    <div className="flex-1 text-xs">
                      <div className="font-medium">{a.internal_ref || a.invoice_number} — {formatCurrency(a.avoir_remaining_amount)} disponible</div>
                    </div>
                    {a.id in avoirSelections && (
                      <Input type="number" step="0.01" min="0" max={a.avoir_remaining_amount}
                        value={avoirSelections[a.id]}
                        onChange={(e) => setAvoirAmount(a.id, e.target.value)}
                        className="w-24 h-8 text-xs" />
                    )}
                  </div>
                ))}
                <div className="border-t border-emerald-200 pt-2 mt-2 text-xs">
                  <div className="flex justify-between"><span>TTC facture</span><strong>{formatCurrency(Math.abs(invoice?.montant_ttc || 0))}</strong></div>
                  <div className="flex justify-between text-emerald-800"><span>Avoirs imputés</span><strong>− {formatCurrency(totalDeducted)}</strong></div>
                  <div className="flex justify-between mt-1 border-t border-emerald-300 pt-1"><span>Montant viré (XML)</span><strong className="text-indigo-900">{formatCurrency(netAmount)}</strong></div>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500">Un fichier pain.001.001.03 sera téléchargé automatiquement. La facture sera marquée payée seulement après votre confirmation à l'étape suivante.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSepaOpen(false)}>Annuler</Button>
            <Button onClick={handleSepaSubmit} disabled={sepaBusy || !sepaForm.bank_account_id} className="bg-indigo-600 hover:bg-indigo-700">
              {sepaBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-SEPA confirmation */}
      <Dialog open={postSepaOpen} onOpenChange={(open) => { setPostSepaOpen(open); if (!open) setPostSepa(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Check className="w-5 h-5 inline mr-2 text-emerald-600" />Clôturer la facture ?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Le fichier SEPA <strong>{postSepa?.reference}</strong> a été généré et téléchargé. Transmettez-le à votre banque puis indiquez à l'application si la facture peut être marquée comme payée.</p>
            <p className="text-xs text-slate-500">Date du paiement (= date d'exécution du virement) : <strong>{postSepa?.date_execution}</strong>.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPostSepaOpen(false); setPostSepa(null); }} disabled={postSepaBusy}>Plus tard</Button>
            <Button onClick={handlePostSepaConfirm} disabled={postSepaBusy} className="bg-emerald-600 hover:bg-emerald-700">
              {postSepaBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Marquer comme payée
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
